/* eslint-disable consistent-return */
import React from 'react';
import { Button, Stack } from '@mui/material';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  getdetectFaceAPI,
  getSubUsersAPI,
  getUserRobotsAPI,
  setSubUserAPI,
  updateSubUserAPI,
  deleteSubUserAPI,
} from '../api/subuser';
import {
  convertImageToCanvas,
  dataURItoBlob,
  getIndex,
  getUserPId,
  isNullOrEmpty,
} from '../common/util';
import {
  FACE_DETECT_RESULTS,
  MODAL_TYPE_DELETE,
  MODAL_TYPE_UPDATE,
} from '../common/consts';
import TableAvatar from '../components/TableAvatar';
import { getFile } from '../api/bot';
import { updateRobotDataAPI } from '../api/robot';

const MODULE_SUBUSER = 'subuser';
const SUBUSER_REGISTER_CARD_INITIAL = {
  user: '',
  nickName: '',
  serial: '',
  serialId: '',
  imageLink: '',
  imageName: '',
  img: '',
  birthDate: '',
  tel: '',
  gender: 'unknown',
  guardiansTel: [],
};
const NEW_ITEMS_INITIAL = {
  list: [],
  groups: [],
  length: 0,
};

const ADD_INITIAL = {
  newItems: { ...NEW_ITEMS_INITIAL },
  itemError: {},
  itemResult: null,
  itemPending: false,
  itemMessage: null,
  robots: [],
  robotList: null,
};

const MODAL_INITIAL = {
  loading: false,
  open: false,
  items: [],
  bDel: false,
  error: null,
  result: null,
  message: null,
};

const initialState = {
  loading: false,
  error: null,
  contentItems: [],
  list: [],
  total: 0,
  add: { ...ADD_INITIAL },
  modal: { ...MODAL_INITIAL },
};

const createListData = (d, i) => {
  const { alias, group, id, name, nickName, serial } = d;

  const avatarName = (
    <Stack direction="row" gap={1} alignItems="center">
      <TableAvatar {...{ src: getFile({ subuserPhoto: id }) }} />
      {name}
    </Stack>
  );
  const buttons = (
    <Stack direction="row">
      <Button
        name={MODAL_TYPE_UPDATE}
        color="secondary"
        size="small"
        variant="contained"
        sx={{ minWidth: 50, mr: 1 }}
      >
        수정
      </Button>
      <Button
        name={MODAL_TYPE_DELETE}
        color="error"
        size="small"
        variant="contained"
        sx={{ minWidth: 50 }}
      >
        삭제
      </Button>
    </Stack>
  );

  const groupName = group && 'name' in group ? group.name : '';
  const item = [
    { key: 'index', content: getIndex(i + 1) },
    { key: 'name', content: avatarName },
    { key: 'nickName', content: nickName },
    { key: 'serial', content: serial },
    { key: 'alias', content: alias },
    { key: 'group', content: groupName },
    { key: 'buttons', content: buttons },
  ];

  return { id, key: id, item, disabled: false };
};

export const getData = createAsyncThunk(
  `${MODULE_SUBUSER}/getData`,
  async (conditions, { rejectWithValue }) => {
    try {
      const { result, list, total } = await getSubUsersAPI({
        ...conditions,
        userId: getUserPId(),
      });
      if (result) {
        if (list.length === 0)
          return rejectWithValue('사용자 목록이 존재하지 않습니다.');
        const contentItems = list.map((item, i) =>
          createListData(item, conditions.desc ? list.length - 1 - i : i),
        );
        return { list, contentItems, total, listLength: 0 };
      }
      return rejectWithValue('사용자 목록 로드 중 에러가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('사용자 목록 로드 중 에러가 발생했습니다.');
    }
  },
);

export const openModal = createAsyncThunk(
  `${MODULE_SUBUSER}/openModal`,
  async ({ items, type }) => {
    const bDel = type === MODAL_TYPE_DELETE;
    if (!bDel) {
      const data = await getUserRobotsAPI({ userId: getUserPId(), type });
      return {
        open: true,
        bDel,
        items,
        robots: data.robots,
      };
    }
    return {
      open: true,
      bDel,
      items,
    };
  },
);

export const searchRobot = createAsyncThunk(
  `${MODULE_SUBUSER}/searchRobot`,
  async (conditions, { rejectWithValue }) => {
    try {
      const userId = getUserPId();
      const { id, value, type } = conditions;
      const data = await getUserRobotsAPI({ userId, type });
      let searchData = [];
      if (value && value !== '' && data.robots.length > 0) {
        const check = value.toLowerCase();
        searchData = data.robots.filter(
          (robot) =>
            (isNullOrEmpty(robot.group)
              ? false
              : robot.group.name.toLowerCase().trim().indexOf(check) > -1) ||
            robot.alias.toLowerCase().trim().indexOf(check) > -1 ||
            robot.serial.toLowerCase().trim().indexOf(check) > -1,
        );
      } else {
        searchData = data.robots;
      }

      return { type, id, searchData };
    } catch (error) {
      return rejectWithValue('로봇 검색 중 에러가 발생했습니다.');
    }
  },
);

export const changeCardItem = createAsyncThunk(
  `${MODULE_SUBUSER}/change`,
  async ({ idx, name, value }, { rejectWithValue, getState }) => {
    try {
      const {
        add: { itemError },
      } = getState().subuser;
      if (name === 'img') {
        const canvas = await convertImageToCanvas(value);
        const dataURL = canvas.toDataURL('image/png', 1);
        const blob = dataURItoBlob(dataURL);
        const formData = new FormData();
        formData.append('img', blob);
        const { result, status } = await getdetectFaceAPI(formData);
        if (result && status !== 1) {
          return rejectWithValue({
            idx,
            name,
            error: FACE_DETECT_RESULTS[status],
          });
        }
        if (!result) {
          return rejectWithValue({
            idx,
            name,
            error: '업로드한 사진의 얼굴 인식 중 오류가 발생했습니다.',
          });
        }
      }

      if (name === 'birthDate') {
        if (!value || moment(value).isAfter(moment())) {
          return rejectWithValue({
            idx,
            name,
            error: '유효한 날짜를 입력하세요.',
          });
        }
      }

      const error = itemError[idx] ? { ...itemError, [idx]: null } : {};
      return { idx, name, value, error };
    } catch (error) {
      return rejectWithValue({
        idx,
        name,
        error: '등록 정보 입력 중 오류가 발생했습니다.',
      });
    }
  },
);

export const saveCardItem = createAsyncThunk(
  `${MODULE_SUBUSER}/save`,
  async (item, { rejectWithValue, getState }) => {
    try {
      const {
        add: {
          newItems: { list: ls },
        },
      } = getState().subuser;
      let list = ls;
      if (item) {
        list = [item];
      }
      const resultList = await list.reduce(async (proAcc, cur) => {
        const acc = await proAcc;
        const {
          name,
          nickName,
          serial,
          serialId,
          img,
          gender,
          birthDate,
          tel,
          guardiansTel,
        } = cur;
        if (name && serial) {
          const fd = new FormData();
          if (img) {
            const canvas = await convertImageToCanvas(img);
            const dataURL = canvas.toDataURL('image/png', 1);
            const blob = dataURItoBlob(dataURL);
            fd.append('img', blob);
          }
          fd.append('serial', serial);
          fd.append('serialId', serialId);
          fd.append('name', name);
          fd.append('nickName', nickName);
          fd.append('userPId', getUserPId());
          fd.append('gender', gender);
          fd.append('birthDate', birthDate);
          fd.append('tel', tel);
          fd.append('guardiansTel', guardiansTel);
          const api = await setSubUserAPI(fd);
          return [...acc, api];
        }
        return acc;
      }, []);

      const result = resultList.reduce(
        (acc, cur) => {
          const { result: r } = cur;
          if (r) {
            return [acc[0] + 1, acc[1]];
          }
          return [acc[0], acc[1] + 1];
        },
        [0, 0],
      );
      let success = '';
      let failed = '';
      if (result[0]) {
        success = `${result[0]}명의 사용자 정보가 등록되었습니다.`;
      }
      if (result[1]) {
        failed = `${result[1]}명의 사용자 정보가 등록에 실패하였습니다.`;
      }
      if (success) {
        return failed ? `${success}\n${failed}` : success;
      }
      return rejectWithValue(failed);
    } catch (error) {
      return rejectWithValue('사용자 정보 등록 중 오류가 발생했습니다.');
    }
  },
);

export const updateCardItem = createAsyncThunk(
  `${MODULE_SUBUSER}/update`,
  async (selected, { rejectWithValue, getState }) => {
    try {
      const {
        modal: { items: list },
      } = getState().subuser;
      const result = await list.reduce(async (proAcc, cur) => {
        const {
          id,
          name,
          nickName,
          serial,
          serialId,
          img,
          gender,
          geo,
          birthDate,
          tel,
          guardiansTel,
        } = cur;
        const acc = await proAcc;
        if (selected.indexOf(id) > -1 && name && serial) {
          const fd = new FormData();
          if (img) {
            const canvas = await convertImageToCanvas(img);
            const dataURL = canvas.toDataURL('image/png', 1);
            const blob = dataURItoBlob(dataURL);
            fd.append('img', blob);
          }
          fd.append('subUserId', id);
          fd.append('serial', serial);
          fd.append('serialId', serialId);
          fd.append('name', name);
          fd.append('nickName', nickName);
          fd.append('userPId', getUserPId());
          fd.append('gender', gender);
          fd.append('birthDate', birthDate);
          fd.append('tel', tel);
          fd.append('guardiansTel', guardiansTel);
          if (geo) {
            await updateRobotDataAPI({
              robotId: serialId,
              updateData: { geo },
            });
          }
          const api = await updateSubUserAPI(fd);
          return [...acc, api];
        }
        return acc;
      }, []);
      return `${result.length}명의 사용자 정보가 수정되었습니다.`;
    } catch (error) {
      return rejectWithValue('사용자 정보 수정 중 오류가 발생했습니다.');
    }
  },
);

export const deleteCardItem = createAsyncThunk(
  `${MODULE_SUBUSER}/delete`,
  async (selected, { rejectWithValue, getState }) => {
    try {
      const {
        modal: { items: list },
      } = getState().subuser;
      const result = await list.reduce(async (proAcc, cur) => {
        const { id, serial } = cur;
        const acc = await proAcc;
        if (selected.indexOf(id) > -1) {
          const api = await deleteSubUserAPI({
            serial,
            subUserId: id,
            userPId: getUserPId(),
          });
          return [...acc, api];
        }
        return acc;
      }, []);
      return `${result.length}명의 사용자 정보가 삭제되었습니다.`;
    } catch (error) {
      return rejectWithValue('사용자 정보 삭제 중 오류가 발생했습니다.');
    }
  },
);

export const changeModalItem = createAsyncThunk(
  `${MODULE_SUBUSER}/changeModal`,
  async ({ idx, name, value }, { rejectWithValue, getState }) => {
    try {
      const {
        add: { itemError },
      } = getState().subuser;
      if (name === 'img') {
        const canvas = await convertImageToCanvas(value);
        const dataURL = canvas.toDataURL('image/png', 1);
        const blob = dataURItoBlob(dataURL);
        const formData = new FormData();
        formData.append('img', blob);
        const { result, status } = await getdetectFaceAPI(formData);
        if (result && status !== 1) {
          return rejectWithValue({
            idx,
            name,
            error: FACE_DETECT_RESULTS[status],
          });
        }
        if (!result) {
          return rejectWithValue({
            idx,
            name,
            error: '업로드한 사진의 얼굴 인식 중 오류가 발생했습니다.',
          });
        }
      }

      const error = itemError[idx] ? { ...itemError[idx], [name]: null } : {};
      return { idx, name, value, error };
    } catch (error) {
      return rejectWithValue({
        idx,
        name,
        error: '등록 정보 입력 중 오류가 발생했습니다.',
      });
    }
  },
);

const setNewCardReducer = (state) => {
  const {
    add: { newItems },
  } = state;
  const { list } = newItems;
  // 초기값은 반드시 spread로 새 자원을 만들어야 함.
  list.push({ ...SUBUSER_REGISTER_CARD_INITIAL });
  newItems.length = list.length;
};

const delNewCardReducer = (state, action) => {
  const {
    add: { newItems },
  } = state;
  const { list } = newItems;
  const { idx } = action.payload;
  list.splice(idx, 1);
  newItems.length = list.length;
};

export const toggleRobot = createAsyncThunk(
  `${MODULE_SUBUSER}/toggleRobot`,
  async ({ value }, { getState }) => {
    const {
      add: { robots },
    } = getState()[MODULE_SUBUSER];
    return robots.filter(({ serial }) => serial !== value);
  },
);

const subuserSlice = createSlice({
  name: MODULE_SUBUSER,
  initialState,
  reducers: {
    initialize: () => ({ ...initialState }),
    setNewCard: setNewCardReducer,
    delNewCard: delNewCardReducer,
    initNewCard: (state) => ({ ...state, add: { ...ADD_INITIAL } }),
    closeModal: (state) => ({
      ...state,
      modal: { ...MODAL_INITIAL },
    }),
  },
  extraReducers: {
    [toggleRobot.fulfilled.type]: (state, action) => ({
      ...state,
      add: { ...state.add, robotList: action.payload },
    }),
    [getData.pending.type]: (state) => ({ ...state, loading: true }),
    [getData.fulfilled.type]: (state, action) => ({
      ...state,
      loading: false,
      ...action.payload,
    }),
    [getData.rejected.type]: (state, action) => ({
      ...state,
      loading: false,
      error: action.payload,
    }),
    [searchRobot.fulfilled.type]: (state, action) =>
      action.payload.type === 'add'
        ? {
            ...state,
            add: {
              ...state.add,
              // newItems: {
              //   ...state.add.newItems,
              //   list: state.add.newItems.list.map((user) =>
              //     user.id === action.payload.id
              //       ? { ...user, searchedRobot: action.payload.searchData }
              //       : user,
              //   ),
              // },
              robots: action.payload.searchData,
              robotList: state.add.robotList || action.payload.searchData,
            },
          }
        : {
            ...state,
            modal: {
              ...state.modal,
              items: state.modal.items.map((user) =>
                user.id === action.payload.id
                  ? { ...user, robotList: action.payload.searchData }
                  : user,
              ),
            },
          },
    [changeCardItem.fulfilled.type]: (state, action) => ({
      ...state,
      add: {
        ...state.add,
        newItems: {
          ...state.add.newItems,
          list: state.add.newItems.list.map((user, idx) =>
            idx === action.payload.idx
              ? { ...user, [action.payload.name]: action.payload.value }
              : user,
          ),
        },
        itemError: {
          ...state.add.itemError,
          ...action.payload.error,
        },
      },
    }),
    [changeCardItem.rejected.type]: (state, action) => ({
      ...state,
      add: {
        ...state.add,
        itemError: {
          [action.payload.idx]: { [action.payload.name]: action.payload.error },
        },
      },
    }),
    [saveCardItem.pending.type]: (state) => ({
      ...state,
      add: {
        ...state.add,
        itemPending: true,
        itemResult: null,
        itemMessage: null,
      },
    }),
    [saveCardItem.fulfilled.type]: (state, action) => ({
      ...state,
      add: {
        ...state.add,
        itemPending: false,
        itemResult: true,
        itemMessage: action.payload,
      },
    }),
    [saveCardItem.rejected.type]: (state, action) => ({
      ...state,
      add: {
        ...state.add,
        itemPending: false,
        itemResult: false,
        itemMessage: action.payload,
      },
    }),
    [openModal.pending.type]: (state) => ({
      ...state,
      modal: { ...MODAL_INITIAL, loading: true },
    }),
    [openModal.fulfilled.type]: (state, action) => ({
      ...state,
      modal: { ...state.modal, loading: false, ...action.payload },
    }),
    [changeModalItem.fulfilled.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        items: state.modal.items.map((user, idx) =>
          idx === action.payload.idx
            ? { ...user, [action.payload.name]: action.payload.value }
            : user,
        ),
      },
    }),
    [changeModalItem.rejected.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        itemError: {
          [action.payload.idx]: { [action.payload.name]: action.payload.error },
        },
      },
    }),
    [updateCardItem.pending.type]: (state) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: true,
        result: null,
        message: null,
        error: null,
      },
    }),
    [updateCardItem.fulfilled.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: true,
        message: action.payload,
      },
    }),
    [updateCardItem.rejected.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: false,
        error: action.payload,
      },
    }),
    [deleteCardItem.pending.type]: (state) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: true,
        result: null,
        message: null,
        error: null,
      },
    }),
    [deleteCardItem.fulfilled.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: true,
        message: action.payload,
      },
    }),
    [deleteCardItem.rejected.type]: (state, action) => ({
      ...state,
      modal: {
        ...state.modal,
        loading: false,
        result: false,
        error: action.payload,
      },
    }),
  },
});

const { reducer: SubuserReducer, actions } = subuserSlice;
export const { initialize, setNewCard, delNewCard, initNewCard, closeModal } =
  actions;

export default SubuserReducer;
